import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import { RefreshCw, ArrowUpCircle } from "lucide-react";

const SEEN_KEY = "app_seen_hash";
const POLL_MS = 60_000;

const VersionContext = createContext({ version: null, updateAvailable: false, applyUpdate: () => {} });

export const useVersion = () => useContext(VersionContext);

async function fetchVersion() {
  try {
    const { data } = await api.get("/app-version");
    return data; // { version, hash }
  } catch {
    return null;
  }
}

async function clearCachesAndReload(hash) {
  try {
    if (window.caches) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* best-effort */
  }
  try {
    localStorage.setItem(SEEN_KEY, hash || "");
  } catch {
    /* ignore */
  }
  const url = new URL(window.location.href);
  url.searchParams.set("_upd", Date.now().toString());
  window.location.replace(url.toString());
}

export function VersionProvider({ children }) {
  const [version, setVersion] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newVersion, setNewVersion] = useState(null);
  const loadedHashRef = useRef(null);
  const latestHashRef = useRef(null);

  const applyUpdate = useCallback(() => {
    clearCachesAndReload(latestHashRef.current);
  }, []);

  useEffect(() => {
    let interval;
    let cancelled = false;

    const init = async () => {
      const data = await fetchVersion();
      if (cancelled || !data || !data.hash) return;

      let seen = null;
      try {
        seen = localStorage.getItem(SEEN_KEY);
      } catch {
        seen = null;
      }

      // Stale cached JS detection: server changed since last full session -> one-time hard reload.
      if (seen && seen !== data.hash) {
        clearCachesAndReload(data.hash);
        return;
      }

      try {
        localStorage.setItem(SEEN_KEY, data.hash);
      } catch {
        /* ignore */
      }
      loadedHashRef.current = data.hash;
      latestHashRef.current = data.hash;
      setVersion(data.version);

      interval = setInterval(async () => {
        const r = await fetchVersion();
        if (!r || !r.hash) return;
        latestHashRef.current = r.hash;
        if (r.hash !== loadedHashRef.current) {
          setNewVersion(r.version);
          setUpdateAvailable(true);
        }
      }, POLL_MS);
    };

    init();
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, []);

  return (
    <VersionContext.Provider value={{ version, updateAvailable, applyUpdate }}>
      {updateAvailable && (
        <div
          data-testid="update-banner"
          className="fixed top-0 inset-x-0 z-[100] bg-blue-600 text-white px-4 py-2.5 flex items-center justify-center gap-3 shadow-md"
        >
          <ArrowUpCircle className="w-4 h-4 shrink-0" />
          <span className="text-sm font-medium">
            È disponibile una nuova versione{newVersion ? ` (V.${newVersion})` : ""}.
          </span>
          <button
            data-testid="update-banner-button"
            onClick={applyUpdate}
            className="inline-flex items-center gap-1.5 text-sm font-semibold bg-white text-blue-700 hover:bg-blue-50 transition-colors rounded-full px-3.5 py-1"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Aggiorna
          </button>
        </div>
      )}
      {children}
    </VersionContext.Provider>
  );
}
